import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Phone,
  Envelope,
  MapPin,
  CircleNotch,
  PaperPlaneTilt,
  CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-24 sm:py-32 contact-dark"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Content */}
          <div className="text-white">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#7A7D82]">
              Get In Touch
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold">
              Ready to Get Your Laser Running Like New?
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              Fill out the form and we'll get back to you within 24 hours. For
              urgent repairs, call us directly.
            </p>

            {/* Contact Info */}
            <div className="mt-10 space-y-6">
              <a
                href="tel:+18016479206"
                className="flex items-center gap-4 text-white hover:text-[#7A7D82] transition-colors group"
              >
                <div className="w-12 h-12 bg-[#7A7D82] flex items-center justify-center group-hover:bg-slate-500 transition-colors">
                  <Phone weight="fill" className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm text-slate-400">Call Us</div>
                  <div className="text-lg font-semibold">(801) 647-9206</div>
                </div>
              </a>

              <a
                href="mailto:info@laserdivision.com"
                className="flex items-center gap-4 text-white hover:text-[#7A7D82] transition-colors group"
              >
                <div className="w-12 h-12 bg-[#7A7D82] flex items-center justify-center group-hover:bg-slate-500 transition-colors">
                  <Envelope weight="fill" className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm text-slate-400">Email Us</div>
                  <div className="text-lg font-semibold">
                    info@laserdivision.com
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-4 text-white">
                <div className="w-12 h-12 bg-[#7A7D82] flex items-center justify-center">
                  <MapPin weight="fill" className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm text-slate-400">Location</div>
                  <div className="text-lg font-semibold">Lakeway, TX 78734</div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="mt-10 pt-10 border-t border-slate-700">
              <h3 className="text-lg font-semibold mb-4">Business Hours</h3>
              <div className="space-y-2 text-slate-300">
                <div className="flex justify-between">
                  <span>Monday - Friday</span>
                  <span>8:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday</span>
                  <span>By Appointment</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday</span>
                  <span>Closed</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-white p-8 lg:p-10">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 bg-green-100 flex items-center justify-center mb-6">
                  <CheckCircle
                    weight="fill"
                    className="w-8 h-8 text-green-600"
                  />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  Message Sent!
                </h3>
                <p className="text-slate-600 mb-6">
                  Thank you for contacting us. We'll get back to you within 24
                  hours.
                </p>
                <Button
                  onClick={() => setSubmitted(false)}
                  variant="outline"
                  className="rounded-none border-2 border-slate-900"
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <h3 className="text-2xl font-bold text-[#1A1A2E] mb-6">
                  Send Us a Message
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#1A1A2E] font-medium">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={handleChange}
                      className="rounded-none border-slate-300 focus:border-[#0B2F6B]"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#1A1A2E] font-medium">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="rounded-none border-slate-300 focus:border-[#0B2F6B]"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-[#1A1A2E] font-medium">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={handleChange}
                    className="rounded-none border-slate-300 focus:border-[#0B2F6B]"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="message"
                    className="text-[#1A1A2E] font-medium"
                  >
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about your laser and the issue you're experiencing..."
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="rounded-none border-slate-300 focus:border-sky-600 resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1E88FF] hover:bg-[#1976D2] text-white rounded-none py-6 text-base font-semibold"
                  data-testid="contact-submit-button"
                >
                  {loading ? (
                    <>
                      <CircleNotch className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <PaperPlaneTilt weight="fill" className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  By submitting this form, you agree to be contacted by John
                  Laser Division regarding your inquiry.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
